import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from "prop-types";
import { Collapse, Container } from 'reactstrap';
import withRouter from '../../Components/Common/withRouter';

import { getBrandLogos } from "../../helpers/brandPortal";
//i18n
import { withTranslation } from "react-i18next";

// Import Data — tenant ou SA conforme a rota (DOC 09 § 4).
import { resolveShellNavdata } from "../resolveShellNavdata";
import {
    findNavGroupForPath,
    syncTwoColumnPanelForPath,
} from "../twoColumnPanel";
import { subscribeAlwaysIconsOnly } from "../../helpers/twoColumnAlwaysIcons";
import VerticalLayout from "../VerticalLayouts";

//SimpleBar
import SimpleBar from "simplebar-react";

const TwoColumnLayout = (props : any) => {
    const logos = getBrandLogos();
    const path = props.router.location.pathname;
    // resolveShellNavdata chama hooks de LayoutMenuData — só no render (nunca em effect).
    const navData = resolveShellNavdata(path);
    const navDataRef = useRef(navData);
    navDataRef.current = navData;

    const activateParentDropdown = useCallback((item : any) => {
        item.classList.add("active");
        let parentCollapseDiv = item.closest(".collapse.menu-dropdown");
        if (parentCollapseDiv) {
            // to set aria expand true remaining
            parentCollapseDiv.classList.add("show");
            parentCollapseDiv.parentElement.children[0].classList.add("active");
            parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
            if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
                parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
                const parentParentCollapse = parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling;
                if (parentParentCollapse) {
                    parentParentCollapse.classList.add("active");
                    if (parentParentCollapse.closest(".collapse.menu-dropdown")) {
                        parentParentCollapse.closest(".collapse.menu-dropdown").classList.add("show");
                    }
                }
            }
            activateIconSidebarActive(parentCollapseDiv.getAttribute("id"));
            return false;
        }
        return false;
    }, []);

    const initMenu = useCallback(() => {
        const pathName = process.env.PUBLIC_URL + path;
        // Ref do render — proibido resolveShellNavdata() aqui (React #321 / hooks).
        const currentNav = navDataRef.current;
        const ul = document.getElementById("navbar-nav") as HTMLElement;
        const items : any = ul.getElementsByTagName("a");
        let itemsArray = [...items]; // converts NodeList to Array
        removeActivation(itemsArray);
        let matchingMenuItem = itemsArray.find((x) => {
            return x.pathname === pathName;
        });
        if (matchingMenuItem) {
            activateParentDropdown(matchingMenuItem);
        } else {
            // Fallback Velzon: marca ícone pelo id do grupo quando o <a> não está no DOM.
            const group = findNavGroupForPath(currentNav, path);
            if (group?.id) {
                activateIconSidebarActive(group.id);
            } else {
                let id: string;
                if (process.env.PUBLIC_URL) {
                    id = pathName.replace(process.env.PUBLIC_URL, '');
                    id = id.replace("/", "");
                } else {
                    id = pathName.replace("/", "");
                }
                if (id) activateIconSidebarActive(id);
            }
        }
        // Folha 1 link (ex.: /dashboard) → só ícones; grupo 2+ → 2ª coluna aberta.
        syncTwoColumnPanelForPath(currentNav, path);
    }, [path, activateParentDropdown]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        initMenu();
    }, [path, initMenu]);

    // Preferência “sempre ícones” (LAC-190) — reaplica collapse sem remount do layout.
    useEffect(() => {
        return subscribeAlwaysIconsOnly(() => {
            syncTwoColumnPanelForPath(navDataRef.current, path);
        });
    }, [path]);

    function activateIconSidebarActive(id  :any) {
        var menu = document.querySelector("#two-column-menu .simplebar-content-wrapper a[sub-items='" + id + "'].nav-icon");
        if (menu !== null) {
            menu.classList.add("active");
        }
    }

    const removeActivation = (items : any) => {
        let activeItems = items.filter((x : any) => x.classList.contains("active"));
        activeItems.forEach((item : any) => {
            if (item.classList.contains("menu-link")) {
                if (!item.classList.contains("active")) {
                    item.setAttribute("aria-expanded", false);
                }
                item.nextElementSibling.classList.remove("show");
            }
            if (item.classList.contains("nav-link")) {
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
                item.setAttribute("aria-expanded", false);
            }
            item.classList.remove("active");
        });

        const ul = document.getElementById("two-column-menu") as HTMLElement;
        const iconItems : any = ul.getElementsByTagName("a");
        let itemsArray = [...iconItems];
        let activeIconItems = itemsArray.filter((x) => x.classList.contains("active"));
        activeIconItems.forEach((item) => {
            item.classList.remove("active");
            var id = item.getAttribute("sub-items");
            var getId = document.getElementById(id) as HTMLElement;
            if (getId)
                getId.classList.remove("show");
        });
    };

    // Resize sidebar
    const [isMenu, setIsMenu] = useState("twocolumn");
    const windowResizeHover = () => {
        initMenu();
        var windowSize = document.documentElement.clientWidth;
        if (windowSize < 767) {
            document.documentElement.setAttribute("data-layout", "vertical");
            setIsMenu('vertical');
        }
        else {
            document.documentElement.setAttribute("data-layout", "twocolumn");
            setIsMenu('twocolumn');
        }
    };

    useEffect(function setupListener() {
        if (props.layoutType === 'twocolumn') {
            window.addEventListener('resize', windowResizeHover);

            // remove classname when component will unmount
            return function cleanupListener() {
                window.removeEventListener('resize', windowResizeHover);
            };
        }
    });
    return (
        <React.Fragment>
            {isMenu === "twocolumn" ?
                <div id="scrollbar">
                    <Container fluid>
                        <div id="two-column-menu">
                            <SimpleBar className="twocolumn-iconview">
                                <Link to="#" className="logo">
                                    {/* logo-sm = símbolo de sinal (menu Two Column); height maior que o envelope antigo */}
                                    <img src={logos.sm} alt={logos.alt} height="28" />
                                </Link>
                                {(navData || []).map((item : any, key : number) => (
                                    <React.Fragment key={key}>
                                        {item.icon && (() => {
                                            // LAC-051 / DOC 09 § 7.3: 1 subitem → navega direto; 2+ → abre 2ª coluna.
                                            // title/aria-label: ícone sozinho não diz o nome do grupo (Carla no 1º uso).
                                            const groupLabel = props.t(item.label);
                                            const subCount = item.subItems?.length ?? 0;
                                            if (subCount === 1) {
                                                const alone = item.subItems[0];
                                                return (
                                                    <li>
                                                        <Link
                                                            to={alone.link || "/#"}
                                                            sub-items={item.id}
                                                            className="nav-icon"
                                                            title={groupLabel}
                                                            aria-label={groupLabel}
                                                        >
                                                            <i className={item.icon} aria-hidden="true"></i>
                                                        </Link>
                                                    </li>
                                                );
                                            }
                                            if (subCount >= 2) {
                                                return (
                                                    <li>
                                                        <Link
                                                            onClick={item.click}
                                                            to="#"
                                                            sub-items={item.id}
                                                            className="nav-icon"
                                                            data-bs-toggle="collapse"
                                                            title={groupLabel}
                                                            aria-label={groupLabel}
                                                        >
                                                            <i className={item.icon} aria-hidden="true"></i>
                                                        </Link>
                                                    </li>
                                                );
                                            }
                                            return (
                                                <Link
                                                    to={item.link ? item.link : "/#"}
                                                    sub-items={item.id}
                                                    className="nav-icon"
                                                    title={groupLabel}
                                                    aria-label={groupLabel}
                                                >
                                                    <i className={item.icon} aria-hidden="true"></i>
                                                </Link>
                                            );
                                        })()}
                                    </React.Fragment>
                                ))}

                            </SimpleBar>
                        </div>
                        <SimpleBar id="navbar-nav" className="navbar-nav">
                            {(navData || []).map((item : any, key : number) => (
                                <React.Fragment key={key}>
                                    {item.subItems ? (
                                        <li className="nav-item">
                                            <Collapse
                                                className="menu-dropdown"
                                                isOpen={item.stateVariables}
                                                id={item.id}>
                                                <ul className="nav nav-sm flex-column">
                                                    {/* subItems  */}
                                                    {item.subItems && ((item.subItems || []).map((subItem : any, key : number) => (
                                                        <React.Fragment key={key}>
                                                            {!subItem.isChildItem ? (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        to={subItem.link ? subItem.link : "/#"}
                                                                        className="nav-link"
                                                                    >
                                                                        {props.t(subItem.label)}
                                                                        {subItem.badgeName ?
                                                                            <span
                                                                                className={"badge badge-pill bg-" + subItem.badgeColor}
                                                                                data-key="t-new"
                                                                                title={subItem.badgeTitle || subItem.badgeName}
                                                                            >{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                </li>
                                                            ) : (
                                                                <li className="nav-item">
                                                                    <Link
                                                                        onClick={subItem.click}
                                                                        className="nav-link"
                                                                        to="/#"
                                                                        data-bs-toggle="collapse"
                                                                        aria-expanded={subItem.stateVariables}
                                                                    > {props.t(subItem.label)}
                                                                        {subItem.badgeName ?
                                                                            <span
                                                                                className={"badge badge-pill bg-" + subItem.badgeColor}
                                                                                data-key="t-new"
                                                                                title={subItem.badgeTitle || subItem.badgeName}
                                                                            >{subItem.badgeName}</span>
                                                                            : null}
                                                                    </Link>
                                                                    <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id={subItem.id}>
                                                                        <ul className="nav nav-sm flex-column">
                                                                            {/* child subItems  */}
                                                                            {subItem.childItems && (
                                                                                (subItem.childItems || []).map((childItem : any, key : number) => (
                                                                                    <React.Fragment key={key}>
                                                                                        {!childItem.isChildItem ? (
                                                                                            <li className="nav-item">
                                                                                                <Link
                                                                                                    to={childItem.link ? childItem.link : "/#"}
                                                                                                    className="nav-link"
                                                                                                >
                                                                                                    {props.t(childItem.label)}
                                                                                                </Link>
                                                                                            </li>
                                                                                        ) : (
                                                                                            <li className="nav-item" key={key}>
                                                                                                <Link
                                                                                                    to={childItem.link ? childItem.link : "/#"}
                                                                                                    onClick={childItem.click}
                                                                                                    data-bs-toggle="collapse"
                                                                                                    aria-expanded={childItem.stateVariables}
                                                                                                    className="nav-link" >
                                                                                                    {props.t(childItem.label)}
                                                                                                </Link>
                                                                                                <Collapse className="menu-dropdown" isOpen={childItem.stateVariables} id={childItem.id}>
                                                                                                    <ul className="nav nav-sm flex-column">
                                                                                                        {/* child subChildItems  */}
                                                                                                        {childItem.isChildItem && (
                                                                                                            (childItem.childItems || []).map((childItem: any, key: number) => (
                                                                                                                <li className="nav-item" key={key} >
                                                                                                                    <Link
                                                                                                                        to={childItem.link ? childItem.link : "/#"}
                                                                                                                        className="nav-link">
                                                                                                                        {props.t(childItem.label)}
                                                                                                                    </Link>
                                                                                                                </li>
                                                                                                            ))
                                                                                                        )}
                                                                                                    </ul>
                                                                                                </Collapse>
                                                                                            </li>
                                                                                        )}
                                                                                    </React.Fragment>
                                                                                ))
                                                                            )}
                                                                        </ul>
                                                                    </Collapse>
                                                                </li>
                                                            )}
                                                        </React.Fragment>
                                                    ))
                                                    )}
                                                </ul>

                                            </Collapse>
                                        </li>
                                    ) : null
                                    }
                                </React.Fragment>
                            ))}
                        </SimpleBar>
                    </Container>
                </div>
                :
                <SimpleBar id="scrollbar" className="h-100">
                    <Container fluid>
                        <div id="two-column-menu"></div>
                        <ul className="navbar-nav" id="navbar-nav">
                            <VerticalLayout />
                        </ul>
                    </Container>
                </SimpleBar>
            }
        </React.Fragment >
    );
};

TwoColumnLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(withTranslation()(TwoColumnLayout));